import { Routes } from '@all-hands-hq/plex-graphql-schema';
import { useHistory } from 'react-router-dom';

type CreateFactDialogState = {
  type: 'new_fact';
  boardId: string;
};
type CreateCardDialogState = {
  type: 'new_card';
  boardId: string;
};
type UpdateFactDialogState = {
  type: 'edit_fact';
  factId: string;
};
type UpdateCardDialogState = {
  type: 'edit_card';
  factId: string;
};
type CreateFactAnswerDialogState = {
  type: 'new_fact_answer';
  factId: string;
};
type UpdateFactAnswerDialogState = {
  type: 'edit_fact_answer';
  factAnswerId: string;
};
type FactAnalyticsFromFactDialogState = {
  type: 'fact_analytics_from_fact';
  factId: string;
};
type FactAnalyticsFromPlexDialogState = {
  type: 'fact_analytics_from_plex';
  factId: string;
};
type CreatePostDialogState = {
  type: 'new_post';
  plexId: string;
};
type UpdatePostDialogState = {
  type: 'edit_post';
  postId: string;
};
type UpdateEventDialogState = {
  type: 'edit_event';
  postId: string;
};
type PostAnalyticsFromPostDialogState = {
  type: 'post_analytics_from_post';
  postId: string;
};
type PostAnalyticsFromPlexDialogState = {
  type: 'post_analytics_from_plex';
  postId: string;
};
type NewPlexDialogState = {
  type: 'new_plex';
};
type EditPlexDialogState = {
  type: 'edit_plex';
  plexId: string;
};
type BrowsePlexesDialogState = {
  type: 'browse_plexes';
};
type CreateEventDialogState = {
  type: 'new_event';
  plexId: string;
};
type CreateBoardDialogState = {
  type: 'new_board';
  plexId: string;
};
type EditBoardDialogState = {
  type: 'edit_board';
  boardId: string;
};
type ImportPostFromGoogleDocsState = {
  type: 'import_gdocs_post';
  plexId: string;
};
type SyncPostFromGoogleDocsState = {
  type: 'sync_gdocs_post';
  postId: string;
};
type ImportFaqsFromGoogleDocsState = {
  type: 'import_gdocs_faqs';
  plexId: string;
};
type ImportFactFromGoogleDocsState = {
  type: 'import_gdocs_fact';
  boardId: string;
};
type SyncBoardFromGoogleDocsState = {
  type: 'sync_gdocs_board';
  boardId: string;
};
type PlexAnalyticsDialogState = {
  type: 'plex_analytics';
  plexId: string;
};
type ImportToTimeline = {
  type: 'import_to_timeline';
  plexId: string;
};
type ImportFaqsFromTemplate = {
  type: 'new_board_from_template';
  plexId: string;
};
type CreateTaskDialogState = {
  type: 'new_task';
  plexId: string;
};
type CreateMilestoneDialogState = {
  type: 'new_milestone';
  plexId: string;
};
type CreateFactFromCommentDialogState = {
  type: 'new_fact_from_comment';
  commentId: string;
};
type CreateResourceReviewProcessDialogState = {
  type: 'new_resource_review_process';
  plexId: string;
};
type EditResourceReviewProcessDialogState = {
  type: 'edit_resource_review_process';
  id: string;
};

export type DialogState =
  | CreateFactDialogState
  | UpdateFactDialogState
  | ImportFactFromGoogleDocsState
  | CreateFactAnswerDialogState
  | UpdateFactAnswerDialogState
  | FactAnalyticsFromFactDialogState
  | FactAnalyticsFromPlexDialogState
  | CreatePostDialogState
  | UpdatePostDialogState
  | UpdateEventDialogState
  | PostAnalyticsFromPostDialogState
  | PostAnalyticsFromPlexDialogState
  | NewPlexDialogState
  | EditPlexDialogState
  | BrowsePlexesDialogState
  | CreateEventDialogState
  | CreateBoardDialogState
  | EditBoardDialogState
  | ImportPostFromGoogleDocsState
  | SyncPostFromGoogleDocsState
  | ImportFaqsFromGoogleDocsState
  | PlexAnalyticsDialogState
  | ImportToTimeline
  | ImportFaqsFromTemplate
  | SyncBoardFromGoogleDocsState
  | CreateTaskDialogState
  | CreateMilestoneDialogState
  | CreateFactFromCommentDialogState
  | CreateResourceReviewProcessDialogState
  | EditResourceReviewProcessDialogState
  | CreateCardDialogState
  | UpdateCardDialogState;

type RouterHistoryMode = 'push' | 'replace';
type Setter = (state: DialogState | null, mode?: RouterHistoryMode) => void;

export function useDialogState(): [DialogState | null, Setter] {
  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const dialogParam = searchParams.get(Routes.PLEX_DIALOG_PARAM);
  const dialogState = dialogParam != null ? decode(dialogParam) : null;

  const setDialogState = (state: DialogState | null, mode: RouterHistoryMode = 'push'): void => {
    const searchParams = new URLSearchParams(history.location.search);
    if (state != null) {
      searchParams.set(Routes.PLEX_DIALOG_PARAM, encode(state));
    } else {
      searchParams.delete(Routes.PLEX_DIALOG_PARAM);
    }

    const set = mode === 'push' ? history.push : history.replace;
    set({
      ...history.location,
      search: searchParams.toString(),
    });
  };

  return [dialogState, setDialogState];
}

function encode(value: DialogState): string {
  return JSON.stringify(value);
}

function decode(param: string): DialogState {
  return JSON.parse(param) as DialogState;
}
